import "./EquitiesPositionViewComponent.scss";
import template from "./EquitiesPositionViewComponent.html";
import {IComponentOptions, IOnDestroy, IOnInit, IScope} from "angular";
import {AdviserPositionsService} from "../service/AdviserPositionsService";
import {QuoteStore} from "../../quote/store/QuoteStore";
import {BehaviorSubject, Observable, ReplaySubject} from "rxjs";
import {
    AdviserEquityPositionViewModel,
    AdviserEquityPositionViewModels
} from "../models/AdviserEquityPositionViewModel";
import {AdviserEquityPositionRecord, AdviserEquityPositionRecords} from "../models/AdviserEquityPositionRecord";
import {buildQuoteRequest} from "../../quote/util/QuoteStoreUtil";
import {QuoteViewModel, QuoteViewModels} from "../../quote/models/QuoteViewModel";
import {IUiGridConstants} from "ui-grid";
import IUiGridTreeBaseConstants = uiGrid.treeBase.IUiGridTreeBaseConstants;
import {UserInterfaceConfigurationService} from "../../services/configuration/UserInterfaceConfigurationService";
import EquityPositionViewGridOptions from "./util/EquityPositionViewGridOptions";
import {Equality, Predicate} from "../../types/GenericTypes";
import {ArrayUtil} from "../../js/util/ArrayUtil";
import {quoteRequestMatcher} from "../../quote/models/QuoteRequest";
import {SystemEventService} from "../../services/system-state/SystemEventService";
import IUiGridExporterConstants = uiGrid.exporter.IUiGridExporterConstants;

export const EquityPositionsView = "tfEquityPositionView";

export function equityPositionsView(): IComponentOptions {
    return {
        template,
        controllerAs: "ctrl",
        controller: EquitiesPositionSearchComponentController
    };
}

export class EquitiesPositionSearchComponentController implements IOnInit, IOnDestroy {

    public readonly gridOptions: any;
    public readonly lastUpdate$: Observable<Date>;

    private gridApi: any;

    private readonly refresh$ = new BehaviorSubject(true);
    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    /** @ngInject */
    public constructor(private readonly $scope: IScope,
                       private readonly adviserPositionsService: AdviserPositionsService,
                       private readonly quoteStore: QuoteStore,
                       private readonly userInterfaceConfigurationService: UserInterfaceConfigurationService,
                       private readonly systemEventService: SystemEventService,
                       gridUtil: any,
                       uiGridConstants: IUiGridConstants,
                       private readonly uiGridExporterConstants: IUiGridExporterConstants,
                       uiGridTreeBaseConstants: IUiGridTreeBaseConstants) {
        this.gridOptions = EquityPositionViewGridOptions(this.$scope,
                                                         this.onRegisterGridApi.bind(this),
                                                         this.userInterfaceConfigurationService,
                                                         this.systemEventService,
                                                         gridUtil,
                                                         uiGridConstants,
                                                         uiGridTreeBaseConstants);
        this.lastUpdate$ = this.refresh$
                               .map(() => new Date())
                               .publishReplay(1)
                               .refCount();
    }

    public $onInit(): void {
        this.refresh$
            .takeUntil(this.destroyed$)
            .switchMap(() => this.retrievePositions())
            .switchMap((positions) => this.mergeWithQuotes(positions))
            .subscribe((models) => {
                this.$scope.$applyAsync(() => this.gridOptions.data = models);
            });
    }

    private onRegisterGridApi(gridApi: any) {
        this.gridApi = gridApi;
    }

    private retrievePositions(): Observable<AdviserEquityPositionRecords> {
        return Observable.fromPromise(this.adviserPositionsService.equityPositions())
                         .catch(() => Observable.of([]));
    }

    private mergeWithQuotes(positions: AdviserEquityPositionRecords): Observable<AdviserEquityPositionViewModels> {
        const requests = positions.map((p) => buildQuoteRequest(p, p.portfolioType));
        const distinctRequests = ArrayUtil.distinct(requests, quoteRequestMatcher());
        return this.quoteStore
                   .scan(distinctRequests)
                   .auditTime(1000)
                   .map((quotes) => this.buildModels(positions, quotes));
    }

    private buildModels(positions: AdviserEquityPositionRecords,
                        quotes: QuoteViewModels): AdviserEquityPositionViewModels {
        return positions.map((p) => {
            const quote = quotes.find(this.isMatchingQuote(p));
            return AdviserEquityPositionViewModel.of(p, quote);
        });
    }

    private isMatchingQuote(position: AdviserEquityPositionRecord): Predicate<QuoteViewModel> {
        return (q) => (q.code === position.code) &&
            (q.exchange === position.exchange) &&
            (position.portfolioType === q.portfolioType);
    }

    public refresh() {
        this.refresh$.next(true);
    }

    public $onDestroy(): void {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    public exportCsv() {
        if (this.gridApi != null) {
            this.gridApi.exporter.csvExport(this.uiGridExporterConstants.ALL, this.uiGridExporterConstants.ALL);
        }
    }

    public exportCsvVisible() {
        if (this.gridApi != null) {
            this.gridApi.exporter.csvExport(this.uiGridExporterConstants.VISIBLE, this.uiGridExporterConstants.VISIBLE);
        }
    }
}
